import * as Sentry from '@sentry/react';

/**
 * List of Firebase Auth error codes that are considered "expected" parts of the
 * authentication flow and should not trigger an error alert in Sentry.
 */
const EXPECTED_AUTH_ERRORS = [
    'auth/wrong-password',
    'auth/user-not-found',
    'auth/invalid-email',
    'auth/invalid-credential',
    'auth/invalid-login-credentials',
    'auth/multi-factor-auth-required',
    'auth/popup-closed-by-user',
    'auth/cancelled-popup-request',
    'auth/popup-blocked',
    'auth/code-expired',
    'auth/invalid-verification-code',
];

/**
 * Captures an authentication error in Sentry with appropriate context and filtering.
 * Expected errors are logged as breadcrumbs to avoid noise, while unexpected
 * errors are captured as full exceptions.
 *
 * @param {Error} error - The error to capture
 * @param {Object} context - Additional context (email, currentStep, tags, etc.)
 */
export const captureAuthError = (error, context = {}) => {
    const errorCode = error?.code || 'unknown';
    
    // Log to console for local debugging
    console.error(`[AuthError] ${errorCode}:`, error);

    if (EXPECTED_AUTH_ERRORS.includes(errorCode)) {
        // Log as breadcrumb for context instead of triggering an alert
        Sentry.addBreadcrumb({
            category: 'auth',
            message: `Handled expected auth error: ${errorCode}`,
            level: 'info',
            data: {
                ...context,
                errorCode,
                errorMessage: error.message,
            },
        });
        return;
    }

    // Capture as full exception with rich context
    Sentry.withScope((scope) => {
        if (context.email) {
            scope.setUser({ email: context.email });
        }
        
        if (context.currentStep) {
            scope.setTag('auth_step', context.currentStep);
        }
        
        scope.setTag('error_code', errorCode);
        
        if (context.tags) {
            scope.setTags(context.tags);
        }

        scope.setContext('auth_info', {
            ...context,
            errorCode,
            message: error.message,
        });

        Sentry.captureException(error);
    });
};

/**
 * Adds a breadcrumb for a transition in the authentication flow.
 *
 * @param {string} step - The step being transitioned to
 * @param {string} email - Current email in the flow
 */
export const logStepTransition = (step, email) => {
    Sentry.addBreadcrumb({
        category: 'auth_flow',
        message: `Step transition: ${step}`,
        level: 'info',
        data: {
            email: email || 'unknown',
            step,
        },
    });
};
